import React from 'react';
import { Sparkles, Monitor, Coffee } from 'lucide-react';
import { usePromotion, MEETING_ROOM_OFFER, DAY_PASS_OFFER } from '../lib/promotions';
import { OFFER_GRADIENT } from './OfferBanner';

const endLabel = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

const OFFER_COPY = {
  [MEETING_ROOM_OFFER]: {
    icon: Monitor,
    title: 'Meeting Rooms 2+2',
    line: (o) => `Book ${o.block || 2} hours, get ${o.block || 2} hours free as Meeting Room Credits.`,
  },
  [DAY_PASS_OFFER]: {
    icon: Coffee,
    title: 'Day Pass: Buy X, Get X Free',
    line: (o) => `Buy ${o.block || 3} or more Day Passes and get the same number free — buy 4, get 4; buy 7, get 7.`,
  },
};

/**
 * Sale banner for listing pages, driven by /promotions/current — shows only
 * live offers. `focus` puts the matching offer first (e.g. 'meeting_room' on
 * the Meeting Room listing); 'all' shows every live offer.
 */
export const SaleOfferBanner = ({ focus = 'all' }) => {
  const promo = usePromotion();
  if (!promo || promo.state !== 'live') return null;

  const wanted = focus === 'meeting_room' || focus === 'conference_room' ? [MEETING_ROOM_OFFER]
    : focus === 'day_pass' ? [DAY_PASS_OFFER]
      : focus === 'dedicated_desk' ? []
        : [MEETING_ROOM_OFFER, DAY_PASS_OFFER];
  const offers = wanted.filter((k) => promo.offers?.[k]?.live);
  if (!offers.length) return null;

  const until = endLabel(promo.ends_at);
  const rewardsUntil = endLabel(promo.reward_valid_until);
  return (
    <div className="relative overflow-hidden rounded-xl mb-6 p-5 text-white shadow-md" style={{ background: OFFER_GRADIENT }} data-testid="sale-offer-banner">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5" />
        <span className="font-heading font-bold text-lg">{promo.name}</span>
        {until && (
          <span className="text-[11px] font-semibold uppercase tracking-wide bg-white/20 px-2 py-0.5 rounded-full">Till {until}</span>
        )}
      </div>
      <div className={`grid gap-3 ${offers.length > 1 ? 'md:grid-cols-2' : ''}`}>
        {offers.map((k) => {
          const copy = OFFER_COPY[k];
          const Icon = copy.icon;
          return (
            <div key={k} className="flex items-start gap-3 bg-white/10 rounded-lg p-3" data-testid={`sale-offer-${k}`}>
              <Icon className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <p className="font-heading font-semibold">{copy.title}</p>
                <p className="text-sm text-white/90">{copy.line(promo.offers[k])}</p>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-white/75 mt-3">
        For logged-in customers paying by card or UPI. Free credits and passes are usable at any Clayworks centre and <strong>valid till {rewardsUntil}</strong>; unused ones expire after that. GST extra.
      </p>
    </div>
  );
};

/** Small tag for a space card: "2+2 · 2 hrs free" / "Buy X, get X free". */
export const SaleOfferTag = ({ bookableType }) => {
  const promo = usePromotion();
  if (!promo || promo.state !== 'live') return null;
  const key = ['meeting_room', 'conference_room'].includes(bookableType) ? MEETING_ROOM_OFFER
    : bookableType === 'day_pass' ? DAY_PASS_OFFER : null;
  if (!key || !promo.offers?.[key]?.live) return null;
  const b = promo.offers[key].block || (key === DAY_PASS_OFFER ? 3 : 2);
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full"
      style={{ background: OFFER_GRADIENT }}
      data-testid="sale-offer-tag"
    >
      <Sparkles className="w-2.5 h-2.5" />
      {key === DAY_PASS_OFFER ? 'Buy X, get X free' : `${b}+${b} · ${b} hrs free`}
    </span>
  );
};
